// v2/hooks/useCart.js — sepet (gercek API)
//
// Dis yuz eskisiyle AYNI: lines, pending, add, setQty, remove, removeMany,
// clear, totals, stockIssues, lastRemoved, undoRemove, dismissUndo, lineUnits.
// Ek olarak: loading, error, yenile, fiyatDegisenler.
//
// Yaklasim: iyimser guncelleme — ekranda aninda degisir, sunucu hata
// dondururse onceki hale geri alinir ve kullaniciya bildirilir.
//
// Uclar:
//   GET  /api/v2/cart/            kalemler + GUNCEL fiyat (sunucu tazeliyor)
//   POST /api/add-to-cart/        ekleme
//   POST /api/update-order/       miktar guncelleme
//   POST /api/remove-from-cart/   silme

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import ses from '../utils/ses';
import { withVat, VAT_RATE } from '../utils/discount';
import { WAREHOUSES } from '../constants/catalog';
import { apiGet, apiPost } from '../utils/api';
import { mapCartItems } from '../utils/mapCartItem';
import { oturumBilgisi } from '../utils/authStore';

const GZT = 'gaziantepStock';
const IST = 'istanbulStock';

/** Satirin toplam adedi */
export const lineUnits = (l) => WAREHOUSES.reduce((s, w) => s + (l.qty[w.key] || 0), 0);

export default function useCart() {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRemoved, setLastRemoved] = useState(null);
  const undoTimer = useRef(null);
  const ilkYukleme = useRef(true);

  useEffect(() => () => clearTimeout(undoTimer.current), []);

  /** Sepeti sunucudan tazele — fiyatlar burada guncelleniyor */
  const yenile = useCallback(async () => {
    if (!oturumBilgisi().girisli) {
      setLines([]);
      setLoading(false);
      return;
    }
    if (ilkYukleme.current) setLoading(true);
    setError(null);
    try {
      const veri = await apiGet('/v2/cart/?durum=tumu');
      setLines(mapCartItems(veri && veri.items));
    } catch (e) {
      setError(e.message || 'Sepet alinamadi');
    } finally {
      ilkYukleme.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => { yenile(); }, [yenile]);

  /** Sunucu hatasinda onceki hale don */
  const geriAl = useCallback((oncekiSatirlar, mesaj) => {
    setLines(oncekiSatirlar);
    setError(mesaj);
  }, []);

  /** Urun ekle — ayni urun varsa backend miktari artirir */
  const add = useCallback(async (product, count = 1, wh = null) => {
    const whKey = wh ? wh.key : WAREHOUSES[0].key;
    const gaziantep = whKey === GZT;
    const { user } = oturumBilgisi();

    try {
      const cevap = await apiPost('/add-to-cart/', {
        kod: user ? user.kod : '',
        urunKodu: product.productCode,
        urunAdi: product.description,
        grupKodu: product.groupCode || '',
        brutFiyat: product.grossPrice || 0,
        netFiyat: product.netPrice || 0,
        indOran: product.discountRate || 0,
        GAUrunAdedi: gaziantep ? count : 0,
        ANUrunAdedi: gaziantep ? 0 : count,
        location: gaziantep ? 'gaziantep' : 'ankara',
        durum: 'Beklemede',
      });
      if (!cevap || cevap.success === false) {
        ses.hata();
        setError((cevap && cevap.message) || 'Ürün sepete eklenemedi');
        return { ok: false, mesaj: (cevap && cevap.message) || 'Ürün sepete eklenemedi' };
      }
      ses.sepet();
      await yenile();
      return { ok: true };
    } catch (e) {
      ses.hata();
      setError(e.message || 'Ürün sepete eklenemedi');
      return { ok: false, mesaj: e.message };
    }
  }, [yenile]);

  /** Depodaki adedi ayarla — ikisi de sifirlanirsa satir silinir */
  const setQty = useCallback(async (key, whKey, value) => {
    const n = Math.max(0, parseInt(value, 10) || 0);
    const onceki = lines;
    const satir = lines.find((l) => l.key === key);
    if (!satir || satir.status !== 'pending') return;

    const yeniQty = { ...satir.qty, [whKey]: n };
    const toplam = (yeniQty[GZT] || 0) + (yeniQty[IST] || 0);

    // Iyimser guncelleme
    if (toplam === 0) {
      setLastRemoved({ lines: [satir], index: lines.indexOf(satir) });
      clearTimeout(undoTimer.current);
      undoTimer.current = setTimeout(() => setLastRemoved(null), 7000);
      setLines((cur) => cur.filter((l) => l.key !== key));
    } else {
      setLines((cur) => cur.map((l) => (l.key === key ? { ...l, qty: yeniQty } : l)));
    }

    try {
      if (toplam === 0) {
        // Uc "ikisi de 0" kabul etmiyor -> satir silinir
        const cevap = await apiPost('/remove-from-cart/', { itemIds: [satir.id] });
        if (!cevap || cevap.success === false) {
          geriAl(onceki, (cevap && cevap.message) || 'Ürün kaldırılamadı');
          setLastRemoved(null);
        }
      } else {
        const cevap = await apiPost('/update-order/', {
          id: satir.id,
          sipKod: satir.sipKod,
          gaUrunAdedi: yeniQty[GZT] || 0,
          anUrunAdedi: yeniQty[IST] || 0,
        });
        if (cevap && cevap.status === 'error') {
          geriAl(onceki, cevap.message || 'Miktar güncellenemedi');
        }
      }
    } catch (e) {
      geriAl(onceki, e.message || 'Miktar güncellenemedi');
      setLastRemoved(null);
    }
  }, [lines, geriAl]);

  /** Tek satir sil */
  const remove = useCallback(async (key) => {
    const onceki = lines;
    const satir = lines.find((l) => l.key === key);
    if (!satir) return;

    setLastRemoved({ lines: [satir], index: lines.indexOf(satir) });
    clearTimeout(undoTimer.current);
    undoTimer.current = setTimeout(() => setLastRemoved(null), 7000);
    setLines((cur) => cur.filter((l) => l.key !== key));

    try {
      const cevap = await apiPost('/remove-from-cart/', { itemIds: [satir.id] });
      if (!cevap || cevap.success === false) {
        geriAl(onceki, (cevap && cevap.message) || 'Ürün kaldırılamadı');
        setLastRemoved(null);
      }
    } catch (e) {
      geriAl(onceki, e.message || 'Ürün kaldırılamadı');
      setLastRemoved(null);
    }
  }, [lines, geriAl]);

  /** Secili satirlari topluca sil */
  const removeMany = useCallback(async (keys) => {
    const set = new Set(keys);
    const onceki = lines;
    const gidenler = lines.filter((l) => set.has(l.key) && l.status === 'pending');
    if (!gidenler.length) return;

    setLastRemoved({ lines: gidenler, bulk: true });
    clearTimeout(undoTimer.current);
    undoTimer.current = setTimeout(() => setLastRemoved(null), 9000);
    setLines((cur) => cur.filter((l) => !set.has(l.key) || l.status === 'transferred'));

    try {
      const cevap = await apiPost('/remove-from-cart/', {
        itemIds: gidenler.map((l) => l.id),
      });
      if (!cevap || cevap.success === false) {
        geriAl(onceki, (cevap && cevap.message) || 'Ürünler kaldırılamadı');
        setLastRemoved(null);
      }
    } catch (e) {
      geriAl(onceki, e.message || 'Ürünler kaldırılamadı');
      setLastRemoved(null);
    }
  }, [lines, geriAl]);

  /** Bekleyen tum satirlari sil */
  const clear = useCallback(async () => {
    const bekleyen = lines.filter((l) => l.status === 'pending');
    if (!bekleyen.length) return;
    await removeMany(bekleyen.map((l) => l.key));
  }, [lines, removeMany]);

  /** Silmeyi geri al — sunucudan silindigi icin yeniden eklenir */
  const undoRemove = useCallback(async () => {
    const snap = lastRemoved;
    if (!snap) return;
    clearTimeout(undoTimer.current);
    setLastRemoved(null);

    try {
      for (const l of snap.lines) {
        const ga = l.qty[GZT] || 0;
        const ist = l.qty[IST] || 0;
        const gaziantep = ga > 0;
        const { user } = oturumBilgisi();
        // eslint-disable-next-line no-await-in-loop
        await apiPost('/add-to-cart/', {
          kod: user ? user.kod : '',
          urunKodu: l.product.productCode,
          urunAdi: l.product.description,
          grupKodu: l.product.groupCode || '',
          brutFiyat: l.product.grossPrice || 0,
          netFiyat: l.product.netPrice || 0,
          indOran: l.product.discountRate || 0,
          GAUrunAdedi: gaziantep ? ga : 0,
          ANUrunAdedi: gaziantep ? 0 : ist,
          location: gaziantep ? 'gaziantep' : 'ankara',
          durum: 'Beklemede',
        });
      }
      await yenile();
    } catch (e) {
      setError(e.message || 'Geri alma başarısız');
      await yenile();
    }
  }, [lastRemoved, yenile]);

  const dismissUndo = useCallback(() => {
    clearTimeout(undoTimer.current);
    setLastRemoved(null);
  }, []);

  /** Toplamlar — sadece duzenlenebilir satirlar */
  const totals = useMemo(() => {
    const active = lines.filter((l) => l.status === 'pending');
    const units = active.reduce((s, l) => s + lineUnits(l), 0);
    const gross = active.reduce((s, l) => s + l.product.grossPrice * lineUnits(l), 0);
    const net = active.reduce((s, l) => s + l.product.netPrice * lineUnits(l), 0);
    const withTax = withVat(net);
    return {
      gross,
      net,
      discount: gross - net,
      discountPct: gross > 0 ? ((gross - net) / gross) * 100 : 0,
      vat: Math.round((withTax - net) * 100) / 100,
      vatRate: VAT_RATE,
      grand: withTax,
      itemCount: active.length,
      unitCount: units,
    };
  }, [lines]);

  /** Depo stogunu asan satirlar */
  const stockIssues = useMemo(() => {
    const out = [];
    lines.forEach((l) => {
      if (l.status !== 'pending') return;
      WAREHOUSES.forEach((w) => {
        const q = l.qty[w.key] || 0;
        const stock = l.product[w.key] || 0;
        if (q > stock) out.push({ line: l, wh: w, stock, qty: q });
      });
    });
    return out;
  }, [lines]);

  const pending = useMemo(() => lines.filter((l) => l.status === 'pending'), [lines]);

  /** Sepet acilinca fiyati degisen urunler — kullaniciya bildirilir */
  const fiyatDegisenler = useMemo(() => lines.filter((l) => l.fiyatDegisti), [lines]);

  /** Siparis ver — secili BEKLEYEN satirlari 'Islemde' durumuna gecirir.
   *  Fiyat ve stok dogrulamasi sunucuda yapilir; istemciden fiyat gonderilmez. */
  const siparisVer = useCallback(async (keys, aciklama = '') => {
    const set = keys && keys.length ? new Set(keys) : null;
    const gidenler = lines.filter(
      (l) => l.status === 'pending' && (!set || set.has(l.key))
    );
    if (!gidenler.length) {
      return { ok: false, mesaj: 'Sipariş verilecek ürün yok' };
    }
    try {
      const cevap = await apiPost('/v2/orders/create/', {
        itemIds: gidenler.map((l) => l.id),
        aciklama,
      });
      if (!cevap || cevap.success === false) {
        await yenile();
        return {
          ok: false,
          mesaj: (cevap && cevap.message) || 'Sipariş oluşturulamadı',
          yetersizStok: (cevap && cevap.yetersizStok) || null,
        };
      }
      await yenile();
      return {
        ok: true,
        kalemSayisi: cevap.kalemSayisi,
        siparisNolari: cevap.siparisNolari || [],
        netToplam: cevap.netToplam,
        mesaj: cevap.message,
      };
    } catch (e) {
      await yenile();
      return { ok: false, mesaj: (e && e.message) || 'Sipariş oluşturulamadı' };
    }
  }, [lines, yenile]);

  return {
    lines, pending, add, setQty, remove, removeMany, clear, totals,
    stockIssues, lastRemoved, undoRemove, dismissUndo, lineUnits,
    loading, error, yenile, fiyatDegisenler, siparisVer,
  };
}
